import * as React from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { Info16Regular, Checkmark16Filled } from "@fluentui/react-icons";
import { FONT_ACCENT, FONT_BODY } from "../schadenmeldung/theme";
import { useStepStyles } from "../schadenmeldung/steps/stepStyles";
import type { Option } from "./copy";
import type { Contact } from "./types";
import { TelefonEingabe } from "../forms/widgets/TelefonEingabe";

const useStyles = makeStyles({
  subHead: {
    marginTop: "26px",
    marginBottom: "12px",
    paddingTop: "16px",
    ...shorthands.borderTop("1px", "solid", "var(--sm-card-border)"),
  },
  subHeadFirst: {
    marginTop: "0",
    paddingTop: "0",
    borderTopStyle: "none",
  },
  subTitle: {
    margin: 0,
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_ACCENT,
    fontSize: "1.25rem",
    fontWeight: 500,
    lineHeight: 1.2,
  },
  block: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  optionalTag: {
    color: "#9c93a4",
    fontWeight: 400,
    fontSize: "0.8rem",
  },
  numberWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  repeatCard: {
    position: "relative",
    ...shorthands.border("1px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("12px"),
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "14px",
    paddingRight: "14px",
    marginBottom: "12px",
  },
  repeatHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  repeatTitle: {
    margin: 0,
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_ACCENT,
    fontWeight: 600,
    fontSize: "1rem",
  },
  removeBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: "#a23b3b",
    fontFamily: FONT_BODY,
    fontWeight: 600,
    fontSize: "0.85rem",
    ...shorthands.border("1px", "solid", "rgba(162,59,59,0.4)"),
    ...shorthands.borderRadius("8px"),
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ":hover": { backgroundColor: "rgba(162,59,59,0.08)" },
  },
  addBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: "var(--sm-accent-soft)",
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_BODY,
    fontWeight: 600,
    fontSize: "0.9rem",
    ...shorthands.border("1.5px", "dashed", "var(--sm-accent)"),
    ...shorthands.borderRadius("10px"),
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ":hover": { backgroundColor: "rgba(247,168,35,0.18)" },
  },
  adorn: {
    flexShrink: 0,
    color: "#6c6171",
    fontFamily: FONT_BODY,
    fontSize: "0.9rem",
    fontWeight: 600,
  },
  // radio / checkbox shared row
  optRow: {
    appearance: "none",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    textAlign: "left",
    backgroundColor: "#fff",
    color: "var(--sm-primary-deep)",
    fontFamily: FONT_BODY,
    fontSize: "0.95rem",
    fontWeight: 500,
    paddingTop: "11px",
    paddingBottom: "11px",
    paddingLeft: "13px",
    paddingRight: "13px",
    ...shorthands.border("1.5px", "solid", "var(--sm-card-border)"),
    ...shorthands.borderRadius("10px"),
    transitionProperty: "border-color, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("var(--sm-primary)"),
    },
  },
  optRowActive: {
    backgroundColor: "var(--sm-accent-soft)",
    ...shorthands.borderColor("var(--sm-accent)"),
    fontWeight: 600,
  },
  marker: {
    flexShrink: 0,
    width: "20px",
    height: "20px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--sm-primary-deep)",
    backgroundColor: "#fff",
    ...shorthands.border("1.5px", "solid", "var(--sm-card-border)"),
  },
  markerRound: { ...shorthands.borderRadius("999px") },
  markerSquare: { ...shorthands.borderRadius("5px") },
  markerActive: {
    backgroundColor: "var(--sm-accent)",
    ...shorthands.borderColor("var(--sm-accent)"),
  },
  optList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  optGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "8px",
    "@media (min-width: 620px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  info: {
    marginTop: "2px",
    backgroundColor: "rgba(247,168,35,0.08)",
    ...shorthands.border("1px", "solid", "var(--sm-accent-soft)"),
    ...shorthands.borderRadius("10px"),
    fontFamily: FONT_BODY,
    color: "var(--sm-primary-deep)",
    fontSize: "0.86rem",
    lineHeight: 1.5,
  },
  infoSummary: {
    cursor: "pointer",
    listStyle: "none",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontWeight: 600,
    color: "var(--sm-primary)",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  infoBody: {
    paddingTop: "0",
    paddingBottom: "12px",
    paddingLeft: "12px",
    paddingRight: "12px",
    color: "#5c5364",
  },
});

export function StepHead({
  eyebrow,
  title,
  hint,
}: {
  eyebrow: string;
  title: string;
  hint?: string;
}) {
  const s = useStepStyles();
  return (
    <div className={s.stepHead}>
      <p className={s.stepEyebrow}>{eyebrow}</p>
      <h1 className={s.stepTitle}>{title}</h1>
      {hint && <p className={s.stepHint}>{hint}</p>}
    </div>
  );
}

export function SubHead({
  title,
  first,
}: {
  title: string;
  first?: boolean;
}) {
  const styles = useStyles();
  return (
    <div className={mergeClasses(styles.subHead, first ? styles.subHeadFirst : "")}>
      <h2 className={styles.subTitle}>{title}</h2>
    </div>
  );
}

export function Block({ children }: { children: React.ReactNode }) {
  const styles = useStyles();
  return <div className={styles.block}>{children}</div>;
}

export function FieldGrid({ children }: { children: React.ReactNode }) {
  const s = useStepStyles();
  return <div className={s.grid2}>{children}</div>;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  optional,
  autoComplete,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  optional?: boolean;
  autoComplete?: string;
  inputMode?: "text" | "email" | "tel" | "numeric";
}) {
  const s = useStepStyles();
  const styles = useStyles();
  return (
    <div className={s.field}>
      <label className={s.label}>
        {label} {optional && <span className={styles.optionalTag}>(optional)</span>}
      </label>
      {type === "tel" ? (
        // Telefon: einheitliches Format „+49 221 123456-78" beim Tippen.
        <TelefonEingabe className={s.input} value={value} placeholder={placeholder} onChange={onChange} />
      ) : (
        <input
          className={s.input}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          inputMode={inputMode}
        />
      )}
    </div>
  );
}

// Numeric field with live 1000s grouping (de-DE). Stores the raw digit string
// in `value`; shows it grouped. Accepts only digits (optionally one decimal
// part). `suffix`/`prefix` render a static adornment label (e.g. "EUR", "km").
export function NumberField({
  label,
  value,
  onChange,
  placeholder,
  optional,
  suffix,
  prefix,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  optional?: boolean;
  suffix?: string;
  prefix?: string;
  maxLength?: number;
}) {
  const s = useStepStyles();
  const styles = useStyles();
  const display = React.useMemo(() => {
    if (!value) return "";
    const digits = value.replace(/\D/g, "");
    if (!digits) return "";
    return new Intl.NumberFormat("de-DE").format(Number(digits));
  }, [value]);
  return (
    <div className={s.field}>
      <label className={s.label}>
        {label} {optional && <span className={styles.optionalTag}>(optional)</span>}
      </label>
      <div className={styles.numberWrap}>
        {prefix && <span className={styles.adorn}>{prefix}</span>}
        <input
          className={s.input}
          type="text"
          inputMode="numeric"
          value={display}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        />
        {suffix && <span className={styles.adorn}>{suffix}</span>}
      </div>
    </div>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const s = useStepStyles();
  return (
    <div className={s.field}>
      <label className={s.label}>{label}</label>
      <textarea
        className={mergeClasses(s.input, s.textarea)}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        maxLength={4000}
      />
    </div>
  );
}

export function RadioGroup({
  label,
  options,
  value,
  onChange,
  columns,
}: {
  label?: string;
  options: Option[];
  value: string | null;
  onChange: (v: string) => void;
  columns?: boolean;
}) {
  const s = useStepStyles();
  const styles = useStyles();
  return (
    <div className={s.field}>
      {label && <label className={s.label}>{label}</label>}
      <div className={columns ? styles.optGrid : styles.optList}>
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              key={o.value}
              type="button"
              className={mergeClasses(styles.optRow, active ? styles.optRowActive : "")}
              onClick={() => onChange(o.value)}
              aria-pressed={active}
            >
              <span
                className={mergeClasses(
                  styles.marker,
                  styles.markerRound,
                  active ? styles.markerActive : "",
                )}
                aria-hidden
              >
                {active && <Checkmark16Filled />}
              </span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CheckboxList({
  label,
  options,
  values,
  onToggle,
  columns,
}: {
  label?: string;
  options: Option[];
  values: string[];
  onToggle: (v: string) => void;
  columns?: boolean;
}) {
  const s = useStepStyles();
  const styles = useStyles();
  return (
    <div className={s.field}>
      {label && <label className={s.label}>{label}</label>}
      <div className={columns ? styles.optGrid : styles.optList}>
        {options.map((o) => {
          const active = values.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              className={mergeClasses(styles.optRow, active ? styles.optRowActive : "")}
              onClick={() => onToggle(o.value)}
              aria-pressed={active}
            >
              <span
                className={mergeClasses(
                  styles.marker,
                  styles.markerSquare,
                  active ? styles.markerActive : "",
                )}
                aria-hidden
              >
                {active && <Checkmark16Filled />}
              </span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// One entry in a repeatable list — bordered card with an optional remove button.
export function RepeatCard({
  title,
  onRemove,
  children,
}: {
  title?: string;
  onRemove?: () => void;
  children: React.ReactNode;
}) {
  const styles = useStyles();
  return (
    <div className={styles.repeatCard}>
      {(title || onRemove) && (
        <div className={styles.repeatHead}>
          {title ? <h3 className={styles.repeatTitle}>{title}</h3> : <span />}
          {onRemove && (
            <button type="button" className={styles.removeBtn} onClick={onRemove}>
              Entfernen
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export function AddButton({ label, onClick }: { label: string; onClick: () => void }) {
  const styles = useStyles();
  return (
    <button type="button" className={styles.addBtn} onClick={onClick}>
      + {label}
    </button>
  );
}

// Unified contact block: split name, phone + mobile, e-mail, optional address /
// function / initials. Which fields show is controlled via `show`. Writes back
// a partial Contact patch so callers can spread it into their answer object.
export interface ContactShow {
  address?: boolean; // Straße / PLZ / Ort
  funktion?: boolean;
  kuerzel?: boolean;
  emailOptional?: boolean;
}

export function ContactBlock({
  value,
  onChange,
  show,
}: {
  value: Contact;
  onChange: (patch: Partial<Contact>) => void;
  show?: ContactShow;
}) {
  return (
    <Block>
      <FieldGrid>
        <TextField label="Vorname" value={value.vorname} onChange={(v) => onChange({ vorname: v })} autoComplete="given-name" />
        <TextField label="Name" value={value.name} onChange={(v) => onChange({ name: v })} autoComplete="family-name" />
      </FieldGrid>
      {show?.address && (
        <>
          <TextField label="Straße, Hausnummer" value={value.strasse} onChange={(v) => onChange({ strasse: v })} optional autoComplete="street-address" />
          <FieldGrid>
            <TextField label="PLZ" value={value.plz} onChange={(v) => onChange({ plz: v })} inputMode="numeric" optional autoComplete="postal-code" />
            <TextField label="Ort" value={value.ort} onChange={(v) => onChange({ ort: v })} optional autoComplete="address-level2" />
          </FieldGrid>
        </>
      )}
      <FieldGrid>
        <TextField label="Telefonnummer" value={value.tel} onChange={(v) => onChange({ tel: v })} type="tel" inputMode="tel" autoComplete="tel" />
        <TextField label="Handynummer" value={value.handy} onChange={(v) => onChange({ handy: v })} type="tel" inputMode="tel" />
      </FieldGrid>
      <FieldGrid>
        <TextField label="E-Mail-Adresse" value={value.email} onChange={(v) => onChange({ email: v })} type="email" inputMode="email" autoComplete="email" optional={show?.emailOptional} />
        {show?.funktion && <TextField label="Funktion" value={value.funktion} onChange={(v) => onChange({ funktion: v })} optional />}
      </FieldGrid>
      {show?.kuerzel && <TextField label="Kürzel" value={value.kuerzel} onChange={(v) => onChange({ kuerzel: v })} optional />}
    </Block>
  );
}

export function InfoBox({
  children,
  summary = "Warum fragen wir das?",
}: {
  children: React.ReactNode;
  summary?: string;
}) {
  const styles = useStyles();
  return (
    <details className={styles.info}>
      <summary className={styles.infoSummary}>
        <Info16Regular />
        {summary}
      </summary>
      <div className={styles.infoBody}>{children}</div>
    </details>
  );
}
